import { createStorageManager } from "app/pages/sales-intelligence/services/storage-manager/factory";
import type { DemoId } from "app/pages/sales-intelligence/types/demo-mode";

/** How long a sent access request suppresses the banner CTA before the user may nudge again. */
export const ACCESS_REQUEST_TTL_MS = 24 * 60 * 60 * 1000;

const localStorageManager = createStorageManager(window.localStorage, (key) => `si:demo-mode:${key}`);

export const demoModeStorage = localStorageManager.configList({
    /**
     * Epoch ms of the last access request, per demo. One entry keyed by `DemoId` rather than one storage key
     * per demo: the key list is static, so a key per demo would make the generic core enumerate every demo.
     * Entries older than `ACCESS_REQUEST_TTL_MS` are read as absent; nothing prunes them.
     */
    accessRequestSentAt: { key: "access-request-sent-at", defaultValue: {} as Record<DemoId, number> },
});

const { item, storage } = demoModeStorage.accessRequestSentAt;

/**
 * Read/write helpers rather than `useStorage`: that hook re-parses on every `getSnapshot`, so an object payload
 * gets a new identity each render and `useSyncExternalStore` never settles. Fine for the boolean flags it was
 * built for, not for this map.
 */
export const readAccessRequestSentAt = (): Record<DemoId, number> => {
    try {
        const raw = storage.getItem(item.key);

        return raw ? JSON.parse(raw) : item.initialValue;
    } catch {
        return item.initialValue;
    }
};

/** Stamps `demoId` as just-requested and returns the new map. Merges against storage, not a held snapshot. */
export const markAccessRequestSent = (demoId: DemoId): Record<DemoId, number> => {
    const next = { ...readAccessRequestSentAt(), [demoId]: Date.now() };

    try {
        storage.setItem(item.key, JSON.stringify(next));
    } catch {
        // A full or unavailable storage only costs the throttle, never the request itself.
    }

    return next;
};
